import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Typography,
} from "@mui/material";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  CloseRounded,
  FullscreenRounded,
  ZoomInRounded,
  ZoomOutRounded,
} from "@mui/icons-material";

function RenderMarkdown({
  answer,
  message = "",
  citationMap,
  setSelectedSource,
}) {
  const transformLinkUri = (uri) => {
    return uri;
  };

  // Parse citations using the provided global citation map
  const processedMessage = useMemo(() => {
    return parseCitations(message, citationMap);
  }, [message, citationMap]);

  // Remove duplicate citations - keep only the first occurrence
  const dedupedMessage = useMemo(() => {
    return removeDuplicateCitations(processedMessage);
  }, [processedMessage]);

  const finalMessage = preprocessMarkdown(dedupedMessage);

  function removeIndentations(markdown) {
    return markdown
      .split("\n")
      .map((line) => line.trimStart())
      .join("\n");
  }

  function removeDuplicateCitations(text) {
    if (!text) return text;

    const seenCitations = new Set();
    const citationRegex = /\[(\d+)\]\(((?:cite|c_id):[^)]+)\)/g;

    return text.replace(citationRegex, (match, number, href) => {
      const citationId = href.replace(/^(?:cite|c_id):/, "");

      // If we've seen this citation before, remove it
      if (seenCitations.has(citationId)) {
        return "";
      }

      // First occurrence - keep it and mark as seen
      seenCitations.add(citationId);
      return match;
    });
  }

  function parseCitations(text = "", globalCitationMap) {
    if (!globalCitationMap || !text) return text;

    let processedText = text;

    // 1) Backward-compat: convert legacy <citation> tags to markdown links
    const legacyRegex = /<(citation|excel-citation)\s+([^>]*?)>(.*?)<\/\1>/gs;
    processedText = processedText.replace(
      legacyRegex,
      (match, tagType, attributes, content) => {
        const idMatch = attributes.match(/id\s*=\s*["']([^"']+)["']/);
        const citationId = idMatch ? idMatch[1] : null;
        const citationNumber = globalCitationMap.get(citationId)?.number || "?";
        return `${content} [${citationNumber}](cite:${citationId})`;
      }
    );

    // 2) Ensure markdown citation links carry correct numbers from map
    // Formats: [n]((cite|c_id):ID) and [n]((cite|c_id):excel:...)
    const mdCitationRegex =
      /\[([0-9]+)\]\(((?:cite|c_id):(?!\))(?:excel:)?[^)]+)\)/g;
    processedText = processedText.replace(mdCitationRegex, (m, n, href) => {
      const id = href.replace(/^(?:cite|c_id):/, "");
      const key = id; // treat the full id string (excel:...) as key
      const num = globalCitationMap.get(key)?.number || n;
      return `[${num}](${href})`;
    });

    // 3) Handle simple [citationId] format and convert to markdown links
    const simpleCitationRegex = /\[([^\]]+)\](?!\()/g;
    processedText = processedText.replace(simpleCitationRegex, (match, id) => {
      // Skip if it contains spaces (likely regular markdown text)
      if (!id.includes(" ") && globalCitationMap.has(id)) {
        const citationInfo = globalCitationMap.get(id);
        return `[${citationInfo.number}](cite:${id})`;
      }
      return match; // Return unchanged if not a citation
    });

    return processedText;
  }

  function preprocessMarkdown(markdown = "") {
    if (!markdown) {
      return "";
    }
    // Remove links starting with "loc://" or "c_id" and clean up commas and new lines
    return markdown
      .replace(/\[(.*?)\]\((loc:\/\/)[^\)]*\)/g, "") // Remove loc:// links
      .replace(/\s*,\s*,/g, ",") // Remove consecutive commas
      .replace(/\s*,\s*\n/g, "\n") // Remove comma before a new line
      .replace(/\n\s*,\s*/g, "\n") // Remove comma after a new line
      .replace(/,\s*$/, ""); // Remove trailing commas
    // .replace(/\n\s*\n/g, "\n"); // Remove extra new lines
  }

  const handleCitationClick = (citation) => {
    setSelectedSource(citation);
  };

  const baseComponents = {
    img: Image,
    a: ({ children, href }) => {
      if (href.startsWith("cite:") || href.startsWith("c_id:")) {
        const citationId = parseInt(href.replace(/^(?:cite|c_id):/, ""));

        const citation = answer?.content_object?.citations?.find(
          (citation) => citation.id === citationId
        );
        if (!citation) return null;

        return (
          <span
            style={{
              cursor: "pointer",
              color: "#1976d2",
              fontWeight: "500",
              marginRight: "4px",
              display: "inline",
            }}
            onClick={() => {
              handleCitationClick(citation);
            }}>
            [{children}]
          </span>
        );
      }
      return href.startsWith("loc://") ? null : <a href={href}>{children}</a>;
    },
    p: ({ children, ...rest }) => {
      return (
        <p {...rest} style={{ margin: 0 }}>
          {children}
        </p>
      );
    },
    h6: ({ children, ...rest }) => {
      return (
        <h6 {...rest} style={{ margin: 0, fontSize: "16px" }}>
          {children}
        </h6>
      );
    },
    h5: ({ children, ...rest }) => {
      return (
        <h5 {...rest} style={{ margin: "4px 0px", fontSize: "16px" }}>
          {children}
        </h5>
      );
    },
    h4: ({ children, ...rest }) => {
      return (
        <h4 {...rest} style={{ margin: "4px 0px", fontSize: "16px" }}>
          {children}
        </h4>
      );
    },
    h3: ({ children, ...rest }) => {
      return (
        <h3 {...rest} style={{ margin: "8px 0px", fontSize: "18px" }}>
          {children}
        </h3>
      );
    },
    h2: ({ children, ...rest }) => {
      return (
        <h2 {...rest} style={{ margin: "8px 0px", fontSize: "18px" }}>
          {children}
        </h2>
      );
    },
    h1: ({ children, ...rest }) => {
      return (
        <h1 {...rest} style={{ margin: "8px 0px", fontSize: "24px" }}>
          {children}
        </h1>
      );
    },
    li: ({ children, ...rest }) => {
      return children ? <li {...rest}>{children}</li> : null;
    },
    br: () => <br />,
  };

  return (
    <Markdown
      remarkPlugins={[remarkGfm]}
      urlTransform={transformLinkUri}
      components={baseComponents}>
      {finalMessage}
    </Markdown>
  );
}

function Image(props) {
  const { ...rest } = props;
  const [open, setOpen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [loading, setLoading] = useState(true);
  const imgRef = useRef(null);

  useEffect(() => {
    if (imgRef.current && imgRef.current.complete) {
      setLoading(false);
    }
  }, []);

  const handleImageLoad = () => {
    setLoading(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleZoomIn = () => {
    setZoom((prevZoom) => Math.min(prevZoom + 0.1, 3)); // Limit max zoom to 3x
  };

  const handleZoomOut = () => {
    setZoom((prevZoom) => Math.max(prevZoom - 0.1, 0.5)); // Limit min zoom to 0.5x
  };

  return (
    <>
      <Box position={"relative"} width={200} height={200} my={2}>
        {loading && (
          <Skeleton
            variant="rectangular"
            width={200}
            height={200}
            sx={{ borderRadius: 3 }}
          />
        )}
        <img
          {...rest}
          ref={imgRef}
          style={{
            width: 200,
            height: 200,
            objectFit: "cover",
            cursor: "pointer",
            borderRadius: 12,
            border: "1px solid rgba(0, 0, 0, 0.3)", // Darker light black border
            display: loading ? "none" : "block",
          }}
          onClick={handleOpen}
          onLoad={handleImageLoad}
        />
        {!loading && (
          <IconButton
            size="small"
            sx={{
              background: "rgba(38, 50, 56, 0.7)", // Lighter background
              color: "white",
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 1,
              "&:hover": {
                background: "rgba(38, 50, 56, 0.9)", // Slightly darker on hover, but still lighter than original
                color: "white",
              },
            }}
            onClick={handleOpen}>
            <FullscreenRounded />
          </IconButton>
        )}
      </Box>
      <Dialog
        open={open}
        fullWidth
        maxWidth="lg"
        onClose={handleClose}
        PaperProps={{ style: { minHeight: "90%" } }}>
        <DialogTitle
          display={"flex"}
          alignItems={"center"}
          justifyContent={"space-between"}>
          <Typography variant="h6">{props["alt"]}</Typography>
          <Box display={"flex"} alignItems={"center"}>
            <IconButton onClick={handleZoomIn}>
              <ZoomInRounded />
            </IconButton>
            <Box mr={1} />
            <IconButton onClick={handleZoomOut}>
              <ZoomOutRounded />
            </IconButton>
            <Box mr={1} />
            <IconButton onClick={handleClose}>
              <CloseRounded />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <img
            {...rest}
            style={{
              transition: "transform 0.3s ease",
              transformOrigin: "center",
              transform: `scale(${zoom})`,
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
              width: "auto",
              height: "auto",
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

export default RenderMarkdown;
